/** "Build with Ask-AI" — describe the wizard in plain words and hand it to the
    Ask-AI agent, whose `wizard-designer` sub-agent writes the model file and
    ends on the engine's own validator (`wizard_verify.py`). The server makes no
    model call of its own: the brief goes to the Ask-AI panel (the same
    `twasta:agent-send-prompt` hand-off the Playbook designer uses), the agent
    asks its questions there, and the designer reloads when the file changes
    (`useAgentRefresh`). */

import { useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { ObjectPicker } from './pickers'
import { useWizardDesign } from './context'
import { Field, Grid2, SelectInput, cardStyle, hintStyle, inputStyle } from './ui'

const DEVICES = [
  { value: '', label: '— let Ask-AI ask —' },
  { value: 'handheld scanner / phone', label: 'Handheld scanner / phone' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'both handheld and desktop', label: 'Both' },
]

export interface WizardBriefInput {
  objName: string
  exists: boolean
  base: string
  mode: string
  device: string
  users: string
  description: string
  details: string
}

/** The brief handed to the Ask-AI agent. Names the sub-agent, the file and the
 *  verify step, and asks for questions FIRST so nothing is guessed. The wizard
 *  is opened from its menu entry, so the agent must not add another one. */
export function buildWizardBrief(b: WizardBriefInput): string {
  const lines = [
    `Use the wizard-designer sub-agent to ${b.exists ? 'REVISE' : 'build'} the Transaction Wizard "${b.objName}" (obj_type U).`,
    `Write Metadata_Model/wizard_models/model_${b.objName}.json (shape {"wizard": {...}}, "obj_name": "${b.objName}").`,
    b.exists ? 'It already exists: change it in place and keep the steps I did not ask to change.' : '',
    b.base ? `Base transaction: ${b.base}.` : 'Base transaction: work it out from the description below, from this project\'s transactions — ask me if more than one fits.',
    b.mode ? `Mode: ${b.mode}.` : '',
    b.device ? `Used on: ${b.device}.` : '',
    b.users.trim() ? `Used by: ${b.users.trim()}.` : '',
    '',
    b.exists ? 'WHAT TO CHANGE:' : 'WHAT THE WIZARD DOES:',
    b.description.trim(),
    b.details.trim() ? `\nDETAILS FROM THE DESIGNER:\n${b.details.trim()}` : '',
    '',
    'Before writing anything, ask me every question you need answered — anything the description leaves open (which record it edits and how it is found, which states it may change, what is scanned and its format, what happens on a mismatch, what to print). Give each question options with a recommended one.',
    'Ground every column, action, issue code and object in this project\'s real models. If the transaction lacks something the wizard needs, say what and which designer adds it — do not work around it.',
    `End on wizard_verify.py ${b.objName} with no errors, then tell me the steps in plain words, the columns a save through it may change, the warnings left, and which roles to grant the wizard.`,
    `The wizard "${b.objName}" is already on the menu — do not add another menu entry.`,
  ]
  return lines.filter((l, i, a) => l !== '' || a[i - 1] !== '').join('\n')
}

export default function AskAiPanel({ objName, exists, waiting, onSend, onClose }: {
  objName: string
  exists: boolean
  waiting: boolean
  onSend: (brief: string) => void
  onClose?: () => void
}) {
  const ctx = useWizardDesign()
  const [base, setBase] = useState(ctx.model.base_object || '')
  const [mode, setMode] = useState(exists ? (ctx.model.mode || '') : '')
  const [device, setDevice] = useState('')
  const [users, setUsers] = useState('')
  const [description, setDescription] = useState('')
  const [details, setDetails] = useState('')
  const ready = description.trim().length > 0 && !waiting && !ctx.readOnly

  const send = () => {
    if (!ready) return
    onSend(buildWizardBrief({ objName, exists, base, mode, device, users, description, details }))
  }

  return (
    <div style={{ ...cardStyle, borderColor: 'var(--color-accent)' }} data-tw-wizard-askai>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Sparkles size={16} style={{ color: 'var(--color-accent)' }} />
        <div style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>
          {exists ? 'Revise this wizard with Ask-AI' : 'Build this wizard with Ask-AI'}
        </div>
        {onClose && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} title="Close">
            <X size={14} />
          </button>
        )}
      </div>
      <Field label={exists ? 'What should change?' : 'What should the wizard do? Describe the steps as the user walks them.'}>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
          disabled={ctx.readOnly}
          placeholder={exists
            ? 'e.g. After the quantity, ask for the bin, and refuse a lot that has expired.'
            : 'e.g. The storekeeper scans the work order, then scans each material label (lot and quantity); a label for an item not on the order is refused; review the lines, confirm and save, then print the issue slip.'}
          style={{ ...inputStyle, height: 'auto', padding: 8, resize: 'vertical', lineHeight: 1.5 }} />
      </Field>
      <Grid2>
        <Field label="Base transaction (optional)">
          <ObjectPicker objType="T" value={base} onChange={setBase} />
        </Field>
        <Field label="Mode (optional)">
          <SelectInput value={mode} onChange={setMode} options={[
            { value: '', label: '— let Ask-AI decide —' },
            { value: 'add', label: 'Add a record' },
            { value: 'edit', label: 'Edit a record' },
            { value: 'add_or_edit', label: 'Edit if found, else add' },
          ]} />
        </Field>
        <Field label="Used on">
          <SelectInput value={device} onChange={setDevice} options={DEVICES} />
        </Field>
        <Field label="Used by (roles or people, optional)">
          <input value={users} onChange={(e) => setUsers(e.target.value)} disabled={ctx.readOnly}
            placeholder="e.g. storekeepers" style={inputStyle} />
        </Field>
      </Grid2>
      <Field label="Anything else Ask-AI should know (optional)">
        <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={2}
          disabled={ctx.readOnly}
          placeholder="Label formats, what to print, which states may be changed, what happens on a mismatch…"
          style={{ ...inputStyle, height: 'auto', padding: 8, resize: 'vertical', lineHeight: 1.5 }} />
      </Field>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
        <div style={{ ...hintStyle, flex: 1, margin: 0 }}>
          {waiting
            ? 'Ask-AI is working on it — answer its questions in the Ask-AI panel. This screen reloads when it writes the wizard.'
            : 'Ask-AI asks its questions in the Ask-AI panel first, then writes the wizard and checks it with the engine’s validator.'}
        </div>
        <button type="button" className="btn btn-primary btn-sm" disabled={!ready} onClick={send}
          data-tw-action="wizard-askai-send">
          <Sparkles size={14} />
          {waiting ? 'Working…' : exists ? 'Revise with Ask-AI' : 'Build with Ask-AI'}
        </button>
      </div>
    </div>
  )
}
